/**
 * Tables of the rich-text editor (layout and data tables, e.g. a signature with the logo left and the text right).
 *
 * TipTap's table nodes, with e-mail output: `border="0" cellpadding="0" cellspacing="0"`, collapsed borders and
 * widths in px on the cells (Outlook ignores `<col>`/CSS-only widths). Three table-wide choices live on the table node
 * and are mirrored into the cells by a plugin, so new rows and cells always follow them:
 *  - borders: none (pure layout, the default), light, full
 *  - padding: compact, normal
 * The vertical alignment is per cell. The same HTML is read back (borders/padding from the first cell), so a table
 * survives the source-mode round trip and the server sanitizer (`signature` profile allows exactly these properties).
 */
import { type CommandProps } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';

export const TABLE_BORDERS = ['none', 'light', 'full'] as const;
export type TableBorders = (typeof TABLE_BORDERS)[number];
export const TABLE_PADDINGS = ['compact', 'normal'] as const;
export type TablePadding = (typeof TABLE_PADDINGS)[number];
export const CELL_ALIGNMENTS = ['top', 'middle', 'bottom'] as const;
export type CellAlignment = (typeof CELL_ALIGNMENTS)[number];

/** Mail content colors (not theme tokens): the same in every mail client. */
const BORDER_CSS: Record<TableBorders, string> = { none: '', light: 'border: 1px solid #d9d9d9', full: 'border: 1px solid #595959' };
const PADDING_CSS: Record<TablePadding, string> = { compact: 'padding: 2px 4px', normal: 'padding: 6px 8px' };

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    tableStyle: {
      /** Sets borders and/or padding of the table around the selection (cells follow). */
      setTableStyle: (style: { borders?: TableBorders; padding?: TablePadding }) => ReturnType;
    };
  }
}

const styleOf = (element: HTMLElement): string => element.getAttribute('style') ?? '';

function parseBorders(element: HTMLElement): TableBorders {
  const border = /(?:^|;)\s*border(?:-top)?\s*:\s*([^;]*)/i.exec(styleOf(element))?.[1] ?? '';
  if (border === '' || /\bnone\b|^\s*0(px)?\s*$/i.test(border)) return 'none';
  return /d9d9d9/i.test(border) ? 'light' : 'full';
}

function parsePadding(element: HTMLElement): TablePadding {
  const px = Number.parseFloat(/(?:^|;)\s*padding\s*:\s*([\d.]+)/i.exec(styleOf(element))?.[1] ?? '');
  return Number.isFinite(px) && px > 2 ? 'normal' : 'compact';
}

const firstCell = (table: HTMLElement): HTMLElement | null => table.querySelector<HTMLElement>('td, th');

function parseWidth(element: HTMLElement): number[] | null {
  const width = Number.parseFloat(element.getAttribute('width') ?? /(?:^|;)\s*width\s*:\s*([\d.]+)px/i.exec(styleOf(element))?.[1] ?? '');
  return Number.isFinite(width) && width > 0 ? [Math.round(width)] : null;
}

/** Cell attributes shared by data and header cells. */
function cellAttributes() {
  const span = (name: 'colspan' | 'rowspan') => ({
    default: 1,
    parseHTML: (element: HTMLElement) => Math.max(1, Number.parseInt(element.getAttribute(name) ?? '1', 10) || 1),
    // Only spans above 1 are written, as in hand-made mail HTML.
    renderHTML: (attributes: Record<string, unknown>) => (Number(attributes[name]) > 1 ? { [name]: String(attributes[name]) } : {}),
  });
  return {
    colspan: span('colspan'),
    rowspan: span('rowspan'),
    borders: {
      default: 'none' as TableBorders,
      parseHTML: (element: HTMLElement) => parseBorders(element),
      renderHTML: (attributes: Record<string, unknown>) => {
        const css = BORDER_CSS[attributes.borders as TableBorders];
        return css ? { style: css } : {};
      },
    },
    padding: {
      default: 'compact' as TablePadding,
      parseHTML: (element: HTMLElement) => parsePadding(element),
      renderHTML: (attributes: Record<string, unknown>) => ({ style: PADDING_CSS[attributes.padding as TablePadding] }),
    },
    valign: {
      default: 'top' as CellAlignment,
      parseHTML: (element: HTMLElement) => {
        const value = element.getAttribute('valign') ?? /(?:^|;)\s*vertical-align\s*:\s*(\w+)/i.exec(styleOf(element))?.[1];
        return (CELL_ALIGNMENTS as readonly string[]).includes(value ?? '') ? value : 'top';
      },
      renderHTML: (attributes: Record<string, unknown>) => ({ valign: attributes.valign as string, style: `vertical-align: ${attributes.valign as string}` }),
    },
    // Width in px as `width` attribute + style (the Outlook way), not as `data-colwidth`.
    colwidth: {
      default: null,
      parseHTML: parseWidth,
      renderHTML: (attributes: Record<string, unknown>) => {
        const width = (attributes.colwidth as number[] | null)?.[0];
        return width ? { width: String(width), style: `width: ${width}px` } : {};
      },
    },
  };
}

const GxTableCell = TableCell.extend({
  addAttributes() {
    return { ...this.parent?.(), ...cellAttributes() };
  },
});

const GxTableHeader = TableHeader.extend({
  addAttributes() {
    return { ...this.parent?.(), ...cellAttributes() };
  },
});

const GxTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      borders: { default: 'none' as TableBorders, parseHTML: (element: HTMLElement) => parseBorders(firstCell(element) ?? element) },
      padding: { default: 'compact' as TablePadding, parseHTML: (element: HTMLElement) => parsePadding(firstCell(element) ?? element) },
    };
  },

  renderHTML({ node }) {
    // A fixed width only when every column of the first row has one (after resizing); else the table sizes itself.
    const widths = node.firstChild ? Array.from({ length: node.firstChild.childCount }, (_, i) => (node.firstChild?.child(i).attrs.colwidth as number[] | null)?.[0]) : [];
    const total = widths.length > 0 && widths.every((w): w is number => typeof w === 'number') ? widths.reduce((a, b) => a + b, 0) : undefined;
    return [
      'table',
      { border: '0', cellpadding: '0', cellspacing: '0', style: `border-collapse: collapse${total ? `; width: ${total}px` : ''}` },
      ['tbody', 0],
    ];
  },

  addCommands() {
    return {
      ...this.parent?.(),
      setTableStyle:
        (style) =>
        ({ state, dispatch }: CommandProps) => {
          const { $from } = state.selection;
          for (let depth = $from.depth; depth > 0; depth--) {
            const node = $from.node(depth);
            if (node.type.name !== 'table') continue;
            if (dispatch) dispatch(state.tr.setNodeMarkup($from.before(depth), undefined, { ...node.attrs, ...style }));
            return true;
          }
          return false;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      ...(this.parent?.() ?? []),
      new Plugin({
        // Cells follow the table's borders/padding, whatever created them (new row, split, paste).
        appendTransaction: (transactions, _old, state) => {
          if (!transactions.some((tr) => tr.docChanged)) return null;
          const tr = state.tr;
          state.doc.descendants((node, pos) => {
            if (node.type.name !== 'table') return true;
            node.descendants((cell, offset) => {
              if (cell.type.spec.tableRole !== 'cell' && cell.type.spec.tableRole !== 'header_cell') return true;
              if (cell.attrs.borders !== node.attrs.borders || cell.attrs.padding !== node.attrs.padding) {
                tr.setNodeMarkup(pos + 1 + offset, undefined, { ...cell.attrs, borders: node.attrs.borders as TableBorders, padding: node.attrs.padding as TablePadding });
              }
              return false;
            });
            return false;
          });
          return tr.docChanged ? tr : null;
        },
      }),
    ];
  },
});

/** Table nodes of the editor schema (resizable columns; `cellMinWidth` keeps cells usable). */
export const tableExtensions = () => [GxTable.configure({ resizable: true, cellMinWidth: 40, lastColumnResizable: false }), TableRow, GxTableHeader, GxTableCell];
